import React, { useState } from 'react'
import {ChevronDown, ChevronUp, HelpCircle} from 'lucide-react'

interface FAQItem {
  question: string
  answer: string
  category: string
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  const faqs: FAQItem[] = [
    {
      category: "Santa Cam",
      question: "What is Santa Cam and how does it work?",
      answer: "Santa Cam is our innovative feature that lets you receive photo updates from Santa's workshop! Throughout December, you'll get exclusive SMS photos showing Santa Cam footage of elves preparing gifts, reindeer training, and Santa checking his list. It's like having a direct video feed to the North Pole, delivered right to your phone. Parents love using Santa Cam as a fun way to keep kids excited about Christmas and encourage good behavior."
    },
    {
      category: "Santa Cam",
      question: "Can I request specific Santa Cam photos?",
      answer: "While we can't control exactly what Santa Cam captures at any given moment (those elves are busy!), you can text requests like 'Show me the reindeer' or 'What's in the workshop?' and we'll send you the most relevant recent Santa Cam footage. All Santa Cam images are authentic North Pole moments!"
    },
    {
      category: "Santa Selfie",
      question: "How do I get a Santa Selfie with my child's name?",
      answer: "Getting a personalized Santa Selfie is easy! Simply text us your child's name and the word 'selfie' and we'll send back a custom Santa Selfie image featuring Santa holding a sign with your child's name. It's perfect for printing, framing, or sharing on social media. Each Santa Selfie is uniquely generated and includes an authentic North Pole backdrop. Many families use their Santa Selfie as their holiday card photo!"
    },
    {
      category: "Santa Selfie",
      question: "Can I get multiple Santa Selfies for different children?",
      answer: "Absolutely! You can request a Santa Selfie for each child in your family. Just text us each name separately, and Santa will create individual Santa Selfie images for every child. There's no limit to how many you can request throughout the holiday season."
    },
    {
      category: "Language Support",
      question: "Do you support Santa Claus in Spanish (Papá Noel)?",
      answer: "¡Sí! We proudly support Santa Claus in Spanish! Text 'Español' to receive all messages from Papá Noel in Spanish. Our bilingual North Pole team ensures that Spanish-speaking families get the same magical experience, including personalized messages, Santa Tracker updates, and Santa Selfie features—all in Spanish. We celebrate the diversity of holiday traditions, including Santa Claus in Spanish-speaking communities around the world."
    },
    {
      category: "Language Support",
      question: "What other languages do you support besides Santa Claus in Spanish?",
      answer: "Currently, we fully support English and Santa Claus in Spanish (Español). We're working on adding French (Père Noël), German (Weihnachtsmann), and Italian (Babbo Natale) for 2026. Our goal is to bring the magic of TextingSanta to families worldwide in their native language!"
    },
    {
      category: "The Santa Clauses Series",
      question: "Is TextingSanta connected to 'The Santa Clauses' 2025 Disney+ series?",
      answer: "While we're not officially affiliated with 'The Santa Clauses' 2025 series starring Tim Allen, we love that the show is bringing Santa into the modern era—just like we are! Fans of The Santa Clauses series will appreciate our tech-forward approach to Christmas magic. We're all about making Santa relevant for today's digital generation, whether through TV shows or text messages!"
    },
    {
      category: "The Santa Clauses Series",
      question: "Can I get updates about The Santa Clauses 2025 season?",
      answer: "We can send you fun reminders when The Santa Clauses 2025 series episodes air! Just text 'TV Updates' and we'll let you know about new episodes, behind-the-scenes content, and how the show connects to real North Pole traditions. It's a great way to combine your love of The Santa Clauses series with our real-time Santa texting service."
    },
    {
      category: "General",
      question: "How is this different from traditional Santa letters?",
      answer: "Unlike USPS Santa Letters that take weeks to arrive, TextingSanta delivers instant Message from Santa responses. You get personalized, two-way conversations throughout December instead of just one annual letter. Plus, you can include Santa Cam photos, Santa Selfie images, and real-time Santa Tracker updates—something impossible with paper mail!"
    },
    {
      category: "General",
      question: "Is TextingSanta safe for my family?",
      answer: "Yes! We use bank-level encryption to protect all communications. We never share phone numbers or personal information with third parties. Parents maintain full control over all interactions, and our content is family-friendly and age-appropriate. We're committed to creating a safe, magical experience for families."
    },
    {
      category: "General",
      question: "How much does it cost?",
      answer: "We offer a free basic plan with limited messages, plus premium plans starting at $9.99/month that include unlimited texting, Santa Cam access, Santa Selfie creation, priority Santa Tracker alerts, and personalized video messages. All plans include our guarantee: if your child isn't delighted, we'll refund your money—no questions asked!"
    },
    {
      category: "General",
      question: "When should I start texting Santa?",
      answer: "You can start anytime! Many families begin in early November to build excitement throughout the entire holiday season. However, Santa's busiest time is December, especially leading up to Christmas Eve when Santa Tracker 2025 goes live with real-time location updates."
    }
  ]

  const categories = Array.from(new Set(faqs.map(faq => faq.category)))

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-950 to-blue-900">
      {/* Hero Section */}
      <section className="py-20 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <HelpCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-white/80">
            Everything you need to know about TextingSanta, Santa Cam, Santa Selfie, and more
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {categories.map((category, catIndex) => (
            <div key={catIndex} className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center">
                <span className="bg-red-600 w-2 h-8 rounded mr-4"></span>
                {category}
              </h2>
              
              <div className="space-y-4">
                {faqs
                  .filter(faq => faq.category === category)
                  .map((faq, index) => {
                    const globalIndex = faqs.indexOf(faq)
                    const isOpen = openIndex === globalIndex

                    return (
                      <div
                        key={globalIndex}
                        className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 overflow-hidden hover:border-red-500/50 transition-all"
                      >
                        <button
                          onClick={() => setOpenIndex(isOpen ? null : globalIndex)}
                          className="w-full px-6 py-5 flex items-center justify-between text-left"
                        >
                          <span className="text-lg font-semibold text-white pr-4">
                            {faq.question}
                          </span>
                          {isOpen ? (
                            <ChevronUp className="w-6 h-6 text-red-400 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-6 h-6 text-white/60 flex-shrink-0" />
                          )}
                        </button>
                        
                        {isOpen && (
                          <div className="px-6 pb-5">
                            <div className="border-t border-white/10 pt-4">
                              <p className="text-white/80 leading-relaxed">
                                {faq.answer}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-20 bg-white/5 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Still Have Questions?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Our Santa's Little Helper support team is here to help!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-red-600 hover:bg-red-500 text-white px-8 py-3 rounded-full font-bold transition-all transform hover:scale-105">
              Text Us Your Question
            </button>
            <button className="bg-white/10 hover:bg-white/20 text-white border-2 border-white/30 px-8 py-3 rounded-full font-bold transition-all">
              Email Support
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}
